/**
 * components/TemplatePicker.js - UI for selecting and inserting section templates
 * Location: /app/frontend/src/admin/page_builder/components/
 * 
 * Modal/panel component that displays available section templates organized
 * by category. Used when admin clicks "Add Section" in Page Builder.
 */

import React, { useState, useMemo } from 'react';
import {
  getCategoriesWithTemplates,
  searchTemplates,
  getTemplateCounts,
  CategoryMeta,
} from '../templates/registry';
import {
  Search,
  Layout,
  Grid3x3,
  BookOpen,
  User,
  Megaphone,
  ChevronRight,
  Plus,
  X,
} from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { ScrollArea } from '../../../components/ui/scroll-area';

// Icon mapping for categories
const CATEGORY_ICONS = {
  hero: Layout,
  content: Grid3x3,
  reader: BookOpen,
  creator: User,
  cta: Megaphone,
};

/**
 * TemplatePicker - Main template selection UI
 */
const TemplatePicker = ({ 
  isOpen, 
  onClose, 
  onSelectTemplate,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Get categories with templates
  const categoriesWithTemplates = useMemo(() => getCategoriesWithTemplates(), []);
  
  // Filter templates by search
  const filteredTemplates = useMemo(() => {
    if (!searchQuery) return null;
    return searchTemplates(searchQuery);
  }, [searchQuery]);
  
  // Get template counts
  const counts = useMemo(() => getTemplateCounts(), []);
  
  // Handle template selection
  const handleSelect = (template) => {
    onSelectTemplate(template);
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div>
            <h2 className="text-lg font-semibold text-white">Add Section</h2>
            <p className="text-sm text-zinc-400">Choose a template to add to your page</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Search */}
        <div className="p-4 border-b border-zinc-800">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..."
              className="pl-10 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Category Sidebar */}
          {!searchQuery && (
            <div className="w-48 border-r border-zinc-800 p-2">
              <ScrollArea className="h-full">
                <div className="space-y-1">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`
                      w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors
                      ${selectedCategory === null 
                        ? 'bg-orange-500/20 text-orange-400' 
                        : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                      }
                    `}
                  >
                    <span className="text-sm font-medium">All Templates</span>
                    <span className="text-xs bg-zinc-800 px-2 py-0.5 rounded-full">
                      {counts.total}
                    </span>
                  </button>
                  
                  <div className="h-px bg-zinc-800 my-2" />
                  
                  {categoriesWithTemplates.map(({ category, meta, templates }) => {
                    const IconComponent = CATEGORY_ICONS[category] || Layout;
                    const isSelected = selectedCategory === category;
                    
                    return (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`
                          w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors
                          ${isSelected 
                            ? 'bg-zinc-800 text-white' 
                            : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-zinc-300'
                          }
                        `}
                      >
                        <IconComponent 
                          className="w-4 h-4 flex-shrink-0" 
                          style={{ color: isSelected ? meta.color : undefined }}
                        />
                        <span className="text-sm flex-1 truncate">{meta.label.replace(' Sections', '').replace(' & Utility', '')}</span>
                        <span className="text-xs text-zinc-500">{templates.length}</span>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          )}
          
          {/* Templates Grid */}
          <div className="flex-1 p-4 overflow-hidden">
            <ScrollArea className="h-full">
              {searchQuery ? (
                // Search results
                <div>
                  <p className="text-sm text-zinc-500 mb-3">
                    {filteredTemplates?.length || 0} results for &ldquo;{searchQuery}&rdquo;
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {filteredTemplates?.map(template => (
                      <TemplateCard 
                        key={template.id} 
                        template={template} 
                        onSelect={() => handleSelect(template)}
                      />
                    ))}
                  </div>
                </div>
              ) : selectedCategory ? (
                // Single category view
                <div>
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-white">
                      {CategoryMeta[selectedCategory]?.label}
                    </h3>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {CategoryMeta[selectedCategory]?.description}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {categoriesWithTemplates
                      .find(c => c.category === selectedCategory)
                      ?.templates.map(template => (
                        <TemplateCard 
                          key={template.id} 
                          template={template} 
                          onSelect={() => handleSelect(template)}
                        />
                      ))
                    }
                  </div>
                </div>
              ) : (
                // All categories view
                <div className="space-y-6">
                  {categoriesWithTemplates.map(({ category, meta, templates }) => (
                    <div key={category}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {React.createElement(CATEGORY_ICONS[category] || Layout, {
                            className: 'w-4 h-4',
                            style: { color: meta.color }
                          })}
                          <h3 className="text-sm font-medium text-white">{meta.label}</h3>
                        </div>
                        <button
                          onClick={() => setSelectedCategory(category)}
                          className="text-xs text-zinc-500 hover:text-orange-400 flex items-center gap-1"
                        >
                          View all
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {templates.slice(0, 4).map(template => (
                          <TemplateCard 
                            key={template.id} 
                            template={template} 
                            onSelect={() => handleSelect(template)}
                            compact
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * TemplateCard - Individual template card in the picker
 */
const TemplateCard = ({ template, onSelect, compact = false }) => {
  const categoryMeta = CategoryMeta[template.category];
  
  return (
    <button
      onClick={onSelect}
      className={`
        group text-left bg-zinc-800/50 border border-zinc-700/50 rounded-lg overflow-hidden
        hover:border-orange-500/50 hover:bg-zinc-800 transition-all
        ${compact ? 'p-3' : 'p-4'}
      `}
    >
      {/* Placeholder thumbnail area */}
      <div 
        className={`
          bg-zinc-900 rounded-md mb-3 flex items-center justify-center
          ${compact ? 'h-16' : 'h-20'}
        `}
        style={{ 
          background: `linear-gradient(135deg, ${categoryMeta?.color}10, transparent)` 
        }}
      >
        <div 
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${categoryMeta?.color}20` }}
        >
          {React.createElement(CATEGORY_ICONS[template.category] || Layout, {
            className: 'w-4 h-4',
            style: { color: categoryMeta?.color }
          })}
        </div>
      </div>
      
      {/* Template info */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h4 className="text-sm font-medium text-white truncate group-hover:text-orange-400 transition-colors">
            {template.name}
          </h4>
          {!compact && (
            <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">
              {template.description}
            </p>
          )}
        </div>
        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Plus className="w-3 h-3 text-white" />
        </div>
      </div>
    </button>
  );
};

/**
 * AddSectionButton - Button that opens the template picker
 */
export const AddSectionButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="
        w-full py-3 px-4 border-2 border-dashed border-zinc-700 rounded-lg
        text-zinc-500 hover:text-orange-400 hover:border-orange-500/50
        flex items-center justify-center gap-2 transition-all
      "
    >
      <Plus className="w-4 h-4" />
      <span className="text-sm font-medium">Add Section</span>
    </button>
  );
};

export default TemplatePicker;
